import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';

import { CommissionOption } from '~/consts/agentHub';

import { useAirlineCarriersSearch } from '~/hooks/apis/flights/useAirlineCarriersSearch';

import { type AirlineCarriersResponse } from '~/services/FlightsService';

type Airline = AirlineCarriersResponse[number];

function commissionOptionToAirline(entry: CommissionOption[number]): Airline {
  return {
    id: 0,
    airlineCode: String(entry.value),
    airlineName: String(entry.label),
    airlineLogo: '',
    airlineBrandMark: '',
    frequentFlyerPartners: [],
  };
}

function airlineToFlightCarrierOption(airline: Airline): CommissionOption[number] {
  return { value: airline.airlineCode, label: airline.airlineName };
}

interface Props {
  brand: string;
  value: CommissionOption;
  onChange: (next: CommissionOption) => void;
}

const NO_OPTIONS_IDLE = 'Start typing to search for carriers';

export default function FlightCarriersInput({ brand, value, onChange }: Props) {
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const setDebouncedSearchDebounced = useMemo(() => debounce((next: string) => setDebouncedSearch(next), 300), []);

  useEffect(() => () => setDebouncedSearchDebounced.cancel(), [setDebouncedSearchDebounced]);

  useEffect(() => {
    setDebouncedSearchDebounced.cancel();
    setDebouncedSearch('');
  }, [brand, setDebouncedSearchDebounced]);

  const trimmedSearch = debouncedSearch.trim();
  const { data, isFetching, isError } = useAirlineCarriersSearch(brand, trimmedSearch);

  const apiRows = useMemo(() => (trimmedSearch ? data ?? [] : []), [trimmedSearch, data]);

  const selectedAirlines = useMemo(() => value.map(commissionOptionToAirline), [value]);

  const options = useMemo(() => {
    const byCode = new Map<string, Airline>();
    for (const a of apiRows) {
      byCode.set(a.airlineCode, a);
    }
    for (const sel of selectedAirlines) {
      if (!byCode.has(sel.airlineCode)) {
        byCode.set(sel.airlineCode, sel);
      }
    }
    return Array.from(byCode.values()).sort((a, b) =>
      a.airlineName.localeCompare(b.airlineName, undefined, { sensitivity: 'base' }),
    );
  }, [apiRows, selectedAirlines]);

  const noOptionsText = useMemo(() => {
    if (!trimmedSearch) {
      return NO_OPTIONS_IDLE;
    }
    if (isError) {
      return 'Failed to fetch carriers';
    }
    if (isFetching) {
      return 'Searching…';
    }
    if (apiRows.length === 0) {
      return 'No matching carriers';
    }
    return NO_OPTIONS_IDLE;
  }, [trimmedSearch, isError, isFetching, apiRows.length]);

  const handleInputChange = useCallback(
    (_: SyntheticEvent, newInputValue: string) => {
      if (!newInputValue.trim()) {
        setDebouncedSearchDebounced.cancel();
        setDebouncedSearch('');
        return;
      }
      setDebouncedSearchDebounced(newInputValue);
    },
    [setDebouncedSearchDebounced],
  );

  const handleChange = useCallback(
    (_: SyntheticEvent, next: Array<Airline>) => {
      onChange(next.map(airlineToFlightCarrierOption));
    },
    [onChange],
  );

  const loading = Boolean(trimmedSearch) && isFetching;

  return (
    <Stack spacing={2}>
      <Box>
        <Autocomplete
          multiple
          loading={loading}
          options={options}
          getOptionLabel={(option) => `${option.airlineName} (${option.airlineCode})`}
          defaultValue={[]}
          filterOptions={(x) => x}
          filterSelectedOptions
          isOptionEqualToValue={(a, b) => a.airlineCode === b.airlineCode}
          renderInput={(params) => (
            <TextField {...params} label="Flight carriers" placeholder={value.length > 0 ? '' : 'Flight carriers'} />
          )}
          onInputChange={handleInputChange}
          value={selectedAirlines}
          onChange={handleChange}
          noOptionsText={noOptionsText}
        />
        <Typography variant="caption" color="textSecondary">
          Start typing to search for carriers
        </Typography>
      </Box>
    </Stack>
  );
}
